<template>
  <div class="artist-search" ref="rootEl">
    <div class="artist-search__input-wrap">
      <input
          type="text"
          class="artist-search__input"
          :placeholder="placeholder"
          v-model="search"
          @focus="onFocus"
          @input="onInput"
          autocomplete="off"
      />
      <button
          v-if="selectedId"
          type="button"
          class="artist-search__clear"
          title="선택 해제"
          @click="clearSelection"
      >
        ×
      </button>
    </div>

    <ul v-if="open" class="artist-search__list">
      <li v-if="filtered.length === 0" class="artist-search__empty">
        검색 결과가 없습니다
      </li>
      <li
          v-for="artist in filtered"
          :key="artist.userAccountId"
          class="artist-search__item"
          :class="{ 'is-selected': String(artist.userAccountId) === String(selectedId) }"
          @mousedown.prevent="select(artist)"
      >
        {{ labelOf(artist) }}
      </li>
      <li v-if="hasMore" class="artist-search__more">
        검색어를 입력해 좁혀주세요 (…외 {{ matchCount - filtered.length }}명)
      </li>
    </ul>

    <a
        v-show="selectedId"
        :href="`https://article21.kr/artist/${selectedId}`"
        target="_blank"
        class="artist-search__link"
    >
      아티스트 페이지
    </a>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, defineProps, defineEmits } from 'vue';
import { apiClient } from '@/services/auth-header';

const MAX_VISIBLE = 50; // 많은 아티스트에서 렌더 폭주 방지

const artistUserAccounts = ref([]);
const search = ref('');
const open = ref(false);
const rootEl = ref(null);

const props = defineProps({
  userAccountId: { type: String, default: '' },
});
const emit = defineEmits(['update:userAccountId']);

const selectedId = computed({
  get: () => props.userAccountId,
  set: (value) => emit('update:userAccountId', value),
});

const placeholder = '아티스트 검색 (이름 · 계정 ID)';

const labelOf = (artist) => `${artist.nameKo || artist.nameEn || ''} (${artist.userAccountId})`;

// 검색어로 필터 (이름 국/영 + 계정 ID, 대소문자 무시). 검색어 없으면 전체.
const matched = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return artistUserAccounts.value;
  return artistUserAccounts.value.filter((a) => {
    const hay = `${a.nameKo || ''} ${a.nameEn || ''} ${a.userAccountId}`.toLowerCase();
    return hay.includes(q);
  });
});
const matchCount = computed(() => matched.value.length);
const filtered = computed(() => matched.value.slice(0, MAX_VISIBLE));
const hasMore = computed(() => matchCount.value > MAX_VISIBLE);

const select = (artist) => {
  selectedId.value = String(artist.userAccountId);
  search.value = labelOf(artist);
  open.value = false;
};

const clearSelection = () => {
  selectedId.value = '';
  search.value = '';
  open.value = true;
};

const onFocus = () => {
  open.value = true;
};

const onInput = () => {
  open.value = true;
  // 입력이 현재 선택 라벨과 달라지면 선택 해제 (검색 모드로 전환)
  const cur = artistUserAccounts.value.find((a) => String(a.userAccountId) === String(selectedId.value));
  if (cur && search.value !== labelOf(cur)) {
    selectedId.value = '';
  }
};

// 바깥 클릭 시 닫기 + 선택값 있으면 라벨 복원
const onDocClick = (e) => {
  if (rootEl.value && !rootEl.value.contains(e.target)) {
    open.value = false;
    restoreLabel();
  }
};

const restoreLabel = () => {
  const cur = artistUserAccounts.value.find((a) => String(a.userAccountId) === String(selectedId.value));
  search.value = cur ? labelOf(cur) : '';
};

const fetchArtistUserAccounts = async () => {
  try {
    const response = await apiClient.get('/users/artists/account-options');
    artistUserAccounts.value = response.data; // List<ArtistSelectOptionDto>
    restoreLabel(); // 수정 모드: 선택된 아티스트 라벨 표시
  } catch (error) {
    console.error('아티스트 목록 조회 실패:', error);
  }
};

// 외부에서 userAccountId 가 바뀌면(초기 로드 등) 라벨 동기화
watch(
    () => props.userAccountId,
    () => {
      if (!open.value) restoreLabel();
    }
);

onMounted(() => {
  fetchArtistUserAccounts();
  document.addEventListener('click', onDocClick);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
});
</script>

<style scoped>
.artist-search {
  position: relative;
  max-width: 540px;
}

.artist-search__input-wrap {
  position: relative;
}

.artist-search__input {
  width: 100%;
  padding: 8px 32px 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.artist-search__input:focus {
  outline: none;
  border-color: #333;
}

.artist-search__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: none;
  background: #eee;
  border-radius: 50%;
  color: #666;
  cursor: pointer;
  line-height: 1;
  font-size: 15px;
}

.artist-search__clear:hover {
  background: #ddd;
}

.artist-search__list {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 280px;
  overflow-y: auto;
}

.artist-search__item {
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
}

.artist-search__item:hover,
.artist-search__item.is-selected {
  background: #f2f4f7;
}

.artist-search__empty,
.artist-search__more {
  padding: 8px 12px;
  font-size: 12px;
  color: #999;
}

.artist-search__link {
  display: inline-block;
  margin-top: 4px;
  font-size: 13px;
}
</style>
